import React, { useEffect, useState } from "react";
import Layout from "./Layout";
import {
  addDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
  deleteDoc,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { Snackbar, Alert } from "@mui/material";
import { db } from "../../../config/firebase";
import { COLLECTION, SESSION } from "../../../data/constant";

function Visitors() {
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(true); // Single loading state for page init
  const [actionLoading, setActionLoading] = useState(false); // For create/update/delete
  const [guards, setGuards] = useState([]);

  // Snackbar State
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showMessage = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        console.log("DEBUG: Starting fetchAllData (PUserTransaction Style)");
        // 1. Retrieve session ID
        const sessionId = localStorage.getItem(SESSION.PUSER_SESSION_ID);
        if (!sessionId) {
          console.warn("DEBUG: No session ID found");
          setLoading(false);
          return;
        }

        // 2. Get Session Data
        const sessionRef = doc(db, COLLECTION.PUSER_SESSION, sessionId);
        const sessionSnap = await getDoc(sessionRef);

        if (!sessionSnap.exists()) {
          console.error("DEBUG: Session doc not found");
          setLoading(false);
          return;
        }

        const { userId } = sessionSnap.data();
        console.log("DEBUG: Found userId:", userId);

        if (!userId) {
          console.error("DEBUG: No userId in session");
          setLoading(false);
          return;
        }

        // 3. Fetch PUser Details (for name/pod info)
        const pusersQuery = query(
          collection(db, COLLECTION.PUSERS),
          where("userId", "==", userId)
        );
        const pusersSnap = await getDocs(pusersQuery);

        if (pusersSnap.empty) {
          console.error("DEBUG: No PUser found");
          setLoading(false);
          return;
        }

        const userDoc = pusersSnap.docs[0];
        const userData = userDoc.data();

        // 4. Fetch Ppod for Pod Number
        let fetchedPpodNumber = "N/A";
        const ppodsQuery = query(
          collection(db, COLLECTION.PPODS),
          where("userId", "==", userId)
        );
        const ppodsSnap = await getDocs(ppodsQuery);
        if (!ppodsSnap.empty) {
          fetchedPpodNumber = ppodsSnap.docs[0].data().ppodNumber;
        }

        const currentUser = {
          documentId: userDoc.id,
          ...userData,
          ppodNumber: fetchedPpodNumber,
          userId: userId // Ensure userId is explicit
        };

        console.log("DEBUG: Setting loggedInUser:", currentUser);
        setLoggedInUser(currentUser);

        // 5. Fetch Visitor Sessions
        console.log("DEBUG: Querying visitor_sessions for userId:", userId);
        const sessionsQuery = query(
          collection(db, COLLECTION.VISITOR_SESSIONS),
          where("userId", "in", [userId, String(userId), Number(userId)])
        );

        const sessionSnapDatas = await getDocs(sessionsQuery);
        console.log("DEBUG: Sessions found:", sessionSnapDatas.size);

        const sessionData = sessionSnapDatas.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .sort((a, b) => {
            const timeA = a.createdAt?.seconds || 0;
            const timeB = b.createdAt?.seconds || 0;
            return timeB - timeA;
          });

        setSessions(sessionData);

      } catch (error) {
        console.error("DEBUG: Error in fetchAllData:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchGuards = async () => {
      try {
        const guardsSnap = await getDocs(collection(db, COLLECTION.GUARDS));
        const guardsList = guardsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGuards(guardsList);
      } catch (error) {
        console.error("Error fetching guards:", error);
      }
    };

    fetchAllData();
    fetchGuards();
  }, []);

  const generateOTP = () =>
    Math.floor(100000 + Math.random() * 900000).toString();

  const createVisitorSession = async (guests, expectedArrival, selectedGuardId) => {
    if (!loggedInUser?.userId) {
      showMessage("User not authenticated.", "error");
      return;
    }
    if (!selectedGuardId) {
      showMessage("Please select a valid Guard.", "warning");
      return;
    }
    setActionLoading(true);

    const payload = {
      userId: loggedInUser.userId,
      podNumber: loggedInUser.ppodNumber || "N/A",
      userName: loggedInUser.userName || "",
      guests,
      otp: generateOTP(),
      status: "CREATED",
      createdAt: serverTimestamp(),
      expectedArrival: expectedArrival || null,
      checkedInAt: null,
      checkedOutAt: null,
      createdBy: loggedInUser.userId,
      guardId: selectedGuardId, // Map session to specific guard
    };

    try {
      await addDoc(collection(db, COLLECTION.VISITOR_SESSIONS), payload);
      setActionLoading(false);
      setOpenModal(false);
      // Refresh only sessions
      refreshSessions(loggedInUser.userId);
      showMessage("Session created successfully!", "success");
    } catch (error) {
      console.error("Error creating session:", error);
      showMessage(`Error creating session: ${error.message}`, "error");
      setActionLoading(false);
    }
  };

  const refreshSessions = async (uid) => {
    try {
      const q = query(
        collection(db, COLLECTION.VISITOR_SESSIONS),
        where("userId", "in", [uid, String(uid), Number(uid)])
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setSessions(data);
    } catch (e) {
      console.error(e);
    }
  };

  const deleteVisitorSession = async (sessionId) => {
    if (!sessionId) return;
    try {
      await deleteDoc(doc(db, COLLECTION.VISITOR_SESSIONS, sessionId));
      refreshSessions(loggedInUser.userId);
      showMessage("Session deleted.", "success");
    } catch (error) {
      showMessage(`Error deleting session: ${error.message}`, "error");
    }
  };

  const updateVisitorSession = async (sessionId, updatedData) => {
    if (!sessionId) return;
    try {
      await updateDoc(doc(db, COLLECTION.VISITOR_SESSIONS, sessionId), updatedData);
      refreshSessions(loggedInUser.userId);
      showMessage("Session updated.", "success");
    } catch (error) {
      showMessage(`Error updating session: ${error.message}`, "error");
    }
  };

  return (
    <>
      <Layout
        sessions={sessions}
        openModal={openModal}
        setOpenModal={setOpenModal}
        createVisitorSession={createVisitorSession}
        deleteVisitorSession={deleteVisitorSession}
        updateVisitorSession={updateVisitorSession}
        loading={actionLoading}
        loadingUser={loading} // Map main loading to Layout's loadingUser
        loggedInUser={loggedInUser}
        guards={guards}
        showMessage={showMessage} // Pass trigger to Layout
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Visitors;
